import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchDisponibiliteJour,
  fetchReservationsBySlot,
  clearReservationDetails,
  selectReservationDetails,
  selectReservationLoadingByKey,
  fetchAteliers,
} from '../../features/reservation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICONS } from '../../utils/fontawesome';
import { selectIsStaffLogged } from '../../redux/staffSlice';
import { deleteReservation as deleteReservationApi, updateReservation as updateReservationApi, createReservation as createReservationApi } from '../../api/reservationApi';
import PlanningAgenda from './components/PlanningAgenda';
import ReservationList from './components/ReservationList';
import DeleteReservationModal from './components/DeleteReservationModal';
import EditReservationModal from './components/EditReservationModal';
import AddReservationCard from './components/AddReservationCard';

const normalizeTimeValue = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    return `${String(Math.max(0, Math.min(23, value))).padStart(2, "0")}:00:00`;
  }
  if (typeof value === "string") {
    if (/^\d{2}:\d{2}:\d{2}$/.test(value)) return value;
    if (/^\d{2}:\d{2}$/.test(value)) return `${value}:00`;
    const trimmed = value.trim();
    if (/^\d{1,2}$/.test(trimmed)) {
      return `${trimmed.padStart(2, "0")}:00:00`;
    }
  }
  return null;
};

const addHoursToTime = (timeStr, hoursToAdd = 0) => {
  if (!timeStr) return null;
  const base = new Date(`1970-01-01T${timeStr}`);
  if (Number.isNaN(base.getTime())) return null;
  base.setHours(base.getHours() + Number(hoursToAdd || 0));
  return base.toTimeString().slice(0, 8);
};

function AdminReservation({ prefillSelection, onPrefillConsumed }) {

  const dispatch = useDispatch();

  const [dateSelectionnee, setDateSelectionnee] = useState(new Date());
  const [atelierSelectionne, setAtelierSelectionne] = useState('');
  const [heureSelectionnee, setHeureSelectionnee] = useState(null);
  const [reservationToDelete, setReservationToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteComment, setDeleteComment] = useState('');
  const [reservationToEdit, setReservationToEdit] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showCancelled, setShowCancelled] = useState(true);
  const [creneauSelectionneData, setCreneauSelectionneData] = useState(null);
  const [zoneSelectionnee, setZoneSelectionnee] = useState(null);

  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const skipResetHeureRef = React.useRef(false);
  const lastPrefillKeyRef = React.useRef(null);

  const creneauJour = useSelector((state) => state.reservation.creneauxJour);
  const reservationDetails = useSelector(selectReservationDetails);
  const loadingByKey = useSelector(selectReservationLoadingByKey);
  const reservationListLoading = loadingByKey?.reservationDetails;
  const isStaffLogged = useSelector(selectIsStaffLogged);
  const ateliers = useSelector((state) => state.reservation.ateliers || []);


  const formatDateForApi = (value) => {
    if (!value) return null;
    const dateObj = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(dateObj.getTime())) return null;
    return `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}-${String(dateObj.getDate()).padStart(2, "0")}`;
  };

  const refreshPlanningAndReservations = useCallback(() => {
    const dateFormate = formatDateForApi(dateSelectionnee);
    if (dateFormate) {
      dispatch(fetchDisponibiliteJour({ date: dateFormate, atelier: atelierSelectionne }));
      dispatch(fetchReservationsBySlot({
        date: dateFormate,
        heure: heureSelectionnee,
        zone: zoneSelectionnee,
      }));
      return;
    }
    dispatch(clearReservationDetails());
  }, [dateSelectionnee, atelierSelectionne, heureSelectionnee, zoneSelectionnee, dispatch]);

  useEffect(() => {
    dispatch(fetchAteliers());
  }, [dispatch]);

  useEffect(() => {
    if (!prefillSelection) return;
    const prefillKey = JSON.stringify(prefillSelection);
    if (lastPrefillKeyRef.current === prefillKey) return;
    lastPrefillKeyRef.current = prefillKey;

    const { date, heure, zone } = prefillSelection;
    let parsedDate = null;
    if (date) {
      const dateObj = typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)
        ? new Date(date + "T00:00:00")
        : new Date(date);
      if (!Number.isNaN(dateObj.getTime())) {
        parsedDate = dateObj;
        setDateSelectionnee(dateObj);
      }
    }
    if (heure) {
      setHeureSelectionnee(heure);
      skipResetHeureRef.current = true;
    }
    if (zone) {
      setZoneSelectionnee(zone);
    } else {
      setZoneSelectionnee(null);
      setCreneauSelectionneData(null);
    }
    const dateForFetch = parsedDate ? formatDateForApi(parsedDate) : null;
    if (dateForFetch) {
      dispatch(fetchDisponibiliteJour({ date: dateForFetch, atelier: atelierSelectionne }));
      dispatch(fetchReservationsBySlot({
        date: dateForFetch,
        heure: heure ?? heureSelectionnee,
        zone: zone ?? null,
      }));
    }
    if (onPrefillConsumed) onPrefillConsumed();
  }, [prefillSelection, onPrefillConsumed, dispatch, atelierSelectionne, heureSelectionnee]);

  useEffect(() => {
    if (!prefillSelection) {
      lastPrefillKeyRef.current = null;
    }
  }, [prefillSelection]);

  const handleDateChange = useCallback((value) => {
    skipResetHeureRef.current = false;
    setZoneSelectionnee(null);
    setCreneauSelectionneData(null);
    setDateSelectionnee(value);
  }, []);

  useEffect(() => {
    const dateFormate = formatDateForApi(dateSelectionnee);
    if (!dateFormate) return;
    dispatch(fetchDisponibiliteJour({ date: dateFormate, atelier: atelierSelectionne }));
  }, [dateSelectionnee, atelierSelectionne, dispatch]);

  useEffect(() => {
    if (skipResetHeureRef.current) {
      skipResetHeureRef.current = false;
      return;
    }
    setHeureSelectionnee(null);
    setZoneSelectionnee(null);
    setCreneauSelectionneData(null);
  }, [dateSelectionnee]);

  useEffect(() => {
    if (!isStaffLogged) {
      dispatch(clearReservationDetails());
      return;
    }
    const dateFormate = formatDateForApi(dateSelectionnee);
    if (!dateFormate) {
      dispatch(clearReservationDetails());
      return;
    }
    dispatch(
      fetchReservationsBySlot({
        date: dateFormate,
        heure: heureSelectionnee,
        zone: zoneSelectionnee,
      })
    );
  }, [dateSelectionnee, heureSelectionnee, zoneSelectionnee, isStaffLogged, dispatch]);

  const handleCreneauSelect = useCallback(
    (slot) => {
      const heure = slot?.heure ?? null;
      setHeureSelectionnee(heure);
      setCreneauSelectionneData(slot?.data || null);
      setZoneSelectionnee(
        slot?.zone ??
        slot?.data?.zone_id ??
        slot?.data?.zoneId ??
        slot?.data?.zone ??
        null
      );
    },
    []
  );

  const handleRequestDelete = useCallback((reservation) => {
    setReservationToDelete(reservation);
    setDeleteError('');
    setDeleteComment('');
    setIsDeleteModalOpen(true);
  }, []);

  const handleRequestEdit = useCallback((reservation) => {
    setReservationToEdit(reservation);
    setIsEditModalOpen(true);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    if (deleteLoading) return;
    setIsDeleteModalOpen(false);
    setReservationToDelete(null);
    setDeleteError('');
  }, [deleteLoading]);

  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setReservationToEdit(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!reservationToDelete?.creneau_id) return;
    setDeleteLoading(true);
    setDeleteError('');
    try {
      await deleteReservationApi(reservationToDelete.creneau_id, {
        commentaire: deleteComment,
      });
      setDeleteLoading(false);
      setIsDeleteModalOpen(false);
      setReservationToDelete(null);
      setDeleteComment('');
      refreshPlanningAndReservations();
    } catch (error) {
      setDeleteError(
        error?.message || "Impossible de supprimer la réservation."
      );
      setDeleteLoading(false);
    }
  }, [reservationToDelete, refreshPlanningAndReservations]);

  const getAtelierDuration = useCallback(
    (atelierId) => {
      if (!atelierId) return null;
      const match =
        ateliers.find(
          (atelier) =>
            String(atelier?.atelier_id ?? atelier?.id) === String(atelierId)
        ) || null;
      const duration = Number(match?.duree);
      return Number.isFinite(duration) && duration > 0 ? duration : null;
    },
    [ateliers]
  );

  const handleCreateReservation = useCallback(
    async ({ atelierId, participants, commentaire, client }) => {
      if (!dateSelectionnee || !heureSelectionnee) {
        setCreateError("Sélectionnez une date et un créneau avant de créer.");
        return;
      }
      setCreateError("");
      setCreateLoading(true);

      const date_atelier = formatDateForApi(dateSelectionnee);
      const startCandidate =
        creneauSelectionneData?.creneau_heure_debut ??
        creneauSelectionneData?.heure_debut ??
        creneauSelectionneData?.heure ??
        heureSelectionnee;
      const creneau_heure_debut = normalizeTimeValue(startCandidate);

      const endCandidate =
        creneauSelectionneData?.creneau_heure_fin ??
        creneauSelectionneData?.heure_fin ??
        creneauSelectionneData?.fin ??
        null;
      let creneau_heure_fin = normalizeTimeValue(endCandidate);
      if (!creneau_heure_fin) {
        const duration = getAtelierDuration(atelierId);
        if (duration && creneau_heure_debut) {
          creneau_heure_fin = addHoursToTime(creneau_heure_debut, duration);
        }
      }

      if (!date_atelier || !creneau_heure_debut || !creneau_heure_fin) {
        setCreateError(
          "Impossible de déterminer la date ou les heures du créneau."
        );
        setCreateLoading(false);
        return;
      }

      const payload = {
        date_atelier,
        creneau_heure_debut,
        creneau_heure_fin,
        nombre_inscrit: Number(participants) || 1,
        status: "RESERVE",
        admin_input: true,
        commentaire: commentaire || "",
        atelier: atelierId,
        client_data: {
          nom: (client?.nom || "").trim(),
          prenom: (client?.prenom || "").trim(),
          email: (client?.email || "").trim(),
          telephone: (client?.telephone || "").trim(),
        },
      };

      try {
        await createReservationApi(payload);
        setCreateLoading(false);
        refreshPlanningAndReservations();
      } catch (error) {
        setCreateError(
          error?.message || "Erreur lors de la création de la réservation."
        );
        setCreateLoading(false);
      }
    },
    [
      dateSelectionnee,
      heureSelectionnee,
      creneauSelectionneData,
      getAtelierDuration,
      refreshPlanningAndReservations,
    ]
  );

  const resolveAtelierId = useCallback(
    (reservation, explicitId) => {
      if (explicitId) return explicitId;
      if (reservation?.atelier_id) return reservation.atelier_id;
      if (reservation?.atelierId) return reservation.atelierId;
      if (reservation?.atelier_uuid) return reservation.atelier_uuid;
      if (reservation?.atelier) {
        const normalizedTarget = reservation.atelier.trim().toLowerCase();
        const match =
          ateliers.find(
            (atelier) =>
              (atelier?.nom || "").trim().toLowerCase() === normalizedTarget
          ) || null;
        if (match) {
          return match.atelier_id ?? match.id ?? match._id ?? "";
        }
      }
      return "";
    },
    [ateliers]
  );

  const handleSubmitEdit = useCallback(
    async ({ reservation, client_data, commentaire, selection, atelierId }) => {
      if (!reservation?.creneau_id) {
        return {
          success: false,
          message: "Réservation introuvable.",
        };
      }

      const targetAtelier = resolveAtelierId(reservation, atelierId);
      if (!targetAtelier) {
        return {
          success: false,
          message: "Impossible de déterminer l'atelier associé.",
        };
      }

      const targetDate =
        selection?.date ||
        reservation?.date_atelier ||
        reservation?.date ||
        null;
      const date_atelier = formatDateForApi(targetDate);
      if (!date_atelier) {
        return {
          success: false,
          message: "Date invalide pour cette réservation.",
        };
      }

      const selectionCreneau = selection?.creneau || null;
      const reservedCreneauId =
        reservation?.creneau_id ??
        reservation?.creneau?.creneau_id ??
        reservation?.creneau?.id ??
        reservation?.id ??
        null;
      const selectedCreneauId =
        selection?.creneauId ??
        selectionCreneau?.creneau_id ??
        selectionCreneau?.id ??
        null;
      const selectionMatchesReservation =
        !selectedCreneauId ||
        !reservedCreneauId ||
        String(selectedCreneauId) === String(reservedCreneauId);

      const selectionHeureDebut =
        selectionCreneau?.creneau_heure_debut ??
        selectionCreneau?.heure_debut ??
        selectionCreneau?.heure ??
        null;
      const reservationHeureDebut =
        reservation?.creneau_heure_debut ??
        reservation?.heure ??
        reservation?.creneau?.heure ??
        null;
      const heureDebutSource = selectionHeureDebut ?? reservationHeureDebut;
      const creneau_heure_debut = normalizeTimeValue(heureDebutSource);
      if (!creneau_heure_debut) {
        return {
          success: false,
          message: "Impossible de déterminer l'heure de début.",
        };
      }

      const selectionHeureFin =
        selectionCreneau?.creneau_heure_fin ??
        selectionCreneau?.heure_fin ??
        selectionCreneau?.fin ??
        null;
      const reservationHeureFin =
        reservation?.creneau_heure_fin ??
        reservation?.heure_fin ??
        reservation?.creneau?.fin ??
        null;
      let creneau_heure_fin = normalizeTimeValue(selectionHeureFin);
      if (
        !creneau_heure_fin &&
        (!selectionCreneau || selectionMatchesReservation)
      ) {
        creneau_heure_fin = normalizeTimeValue(reservationHeureFin);
      }
      if (!creneau_heure_fin) {
        const duration = getAtelierDuration(targetAtelier);
        if (duration) {
          creneau_heure_fin = addHoursToTime(creneau_heure_debut, duration);
        }
      }
      if (!creneau_heure_fin) {
        return {
          success: false,
          message: "Impossible de déterminer l'heure de fin.",
        };
      }

      const requestedParticipants = Number(
        selection?.participants ?? reservation?.nombre_inscrit ?? 1
      );
      const nombre_inscrit =
        Number.isFinite(requestedParticipants) && requestedParticipants > 0
          ? requestedParticipants
          : 1;

      const existingClient = reservation?.client_data || {};
      const payload = {
        date_atelier,
        creneau_heure_debut,
        creneau_heure_fin,
        nombre_inscrit,
        status: reservation?.status || "RESERVE",
        admin_input: Boolean(reservation?.admin_input),
        commentaire: commentaire ?? reservation?.commentaire ?? "",
        atelier: targetAtelier,
        client_data: {
          nom: (client_data?.nom ?? existingClient.nom ?? "").trim(),
          prenom: (client_data?.prenom ?? existingClient.prenom ?? "").trim(),
          email: (client_data?.email ?? existingClient.email ?? "").trim(),
          telephone: (client_data?.telephone ?? existingClient.telephone ?? "").trim(),
        },
      };

      try {
        await updateReservationApi(reservation.creneau_id, undefined, payload);
        refreshPlanningAndReservations();
        handleCloseEditModal();
        return {
          success: true,
          message: "Réservation mise à jour avec succès.",
        };
      } catch (error) {
        return {
          success: false,
          message: error?.message || "Erreur lors de la mise à jour.",
        };
      }
    },
    [
      resolveAtelierId,
      getAtelierDuration,
      refreshPlanningAndReservations,
      handleCloseEditModal,
      formatDateForApi,
    ]
  );

  return (
    <div className="flex flex-col md:flex-row md:justify-between gap-4 md:gap-8 w-full">
      <div className='bg-white p-4 w-full md:w-2/5 rounded-2xl shadow-md shadow-gray-700'>
        <PlanningAgenda
          selectedDate={dateSelectionnee}
          onDateChange={handleDateChange}
          creneaux={creneauJour}
          onSelectCreneau={handleCreneauSelect}
        />
      </div>

      <div className="bg-white p-4 w-full md:w-3/5 rounded-2xl shadow-md shadow-gray-700">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-[#F36D36] text-2xl font-bold">Liste des reservations</h2>
          <button
            type="button"
            onClick={() => setShowCancelled((prev) => !prev)}
            className="flex items-center gap-2 text-xs text-slate-600 border border-slate-200 px-3 py-1.5 rounded-lg hover:border-slate-400 hover:text-slate-900 transition-colors"
          >
            <FontAwesomeIcon icon={showCancelled ? ICONS.eyeSlash : ICONS.eye} className="h-4 w-4" />
            <span>
              {showCancelled ? "Masquer les créneaux annulés" : "Afficher les créneaux annulés"}
            </span>
          </button>
        </div>
        <p className="text-slate-500 text-xs mb-4">
          {heureSelectionnee
            ? `Créneau sélectionné : ${heureSelectionnee}`
            : 'Toutes les réservations pour la date sélectionnée'}
        </p>
        <ReservationList
          reservations={reservationDetails}
          isStaffLogged={isStaffLogged}
          loading={reservationListLoading}
          selectedHour={heureSelectionnee}
          showCancelled={showCancelled}
          onRequestDelete={handleRequestDelete}
          onRequestEdit={handleRequestEdit}
          canAdd={Boolean(dateSelectionnee && heureSelectionnee)}
          addCard={
            Boolean(dateSelectionnee && heureSelectionnee) ? (
              <AddReservationCard
                ateliers={ateliers}
                creneau={creneauSelectionneData}
                loading={createLoading}
                onCreate={handleCreateReservation}
              />
            ) : null
          }
        />
        {createError && (
          <p className="text-xs text-red-600 mt-2">{createError}</p>
        )}
      </div>

      <DeleteReservationModal
        open={isDeleteModalOpen}
        reservation={reservationToDelete}
        onCancel={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
        errorMessage={deleteError}
        comment={deleteComment}
        onCommentChange={setDeleteComment}
      />

      <EditReservationModal
        open={isEditModalOpen}
        reservation={reservationToEdit}
        onClose={handleCloseEditModal}
        onSubmit={handleSubmitEdit}
      />

    </div>
  )
}

export default AdminReservation
